// Fall Enrollment (EF) Step 2 (Configure Parameters). Display and validation only: the institution
// setup comes from System Manager through select-items, and nothing here reads student data.
import { getReportingYear } from './reportingYears';

export const EF_INITIAL_PARAMETERS = {
    collectionYear: '',
    fallTermId: '',
    // Fixed Length is the only format Elements builds for EF, so it is not a choice.
    outputFormat: 'FIXED',
    fallReportingDate: null,
    // Optional parts are chosen on every run; they never carry over to the next one.
    includePartB: false,
    includePartC: false,
    fteInstructionalStaff: ''
};

// IPEDS Institution Type (System Manager > Configuration > General 2) → EF form variant. The two
// non-degree-granting two-year types share EF3, and the two less-than-two-year types share EF4.
const EF_TEMPLATES = { 1: 'EF1', 2: 'EF2', 3: 'EF3', 4: 'EF3', 5: 'EF4', 6: 'EF4' };

export const efTemplateFor = (institutionType) => EF_TEMPLATES[institutionType] || null;

// IPEDS Calendar System IDs (StaticDataProvider.GetIPEDSCalendarSystem).
export const CALENDAR_OTHER_ACADEMIC_YEAR = 5;
const ACADEMIC_REPORTER_CALENDARS = [1, 2, 3, 4];
const PROGRAM_REPORTER_CALENDARS = [CALENDAR_OTHER_ACADEMIC_YEAR, 6, 7];

/** Semester, Quarter, Trimester and Four-one-four report as of a single fall date. */
export const isAcademicReporter = (calendarSystemId) => ACADEMIC_REPORTER_CALENDARS.includes(calendarSystemId);

/** The other calendars report everyone enrolled between August 1 and October 31. */
export const isProgramReporter = (calendarSystemId) => PROGRAM_REPORTER_CALENDARS.includes(calendarSystemId);

/** The fall a collection year reports: 2026–27 reports Fall 2026. */
export const fallYearOf = (collectionYear) => getReportingYear(collectionYear)?.startYear || null;

/** October 15 of the fall year, in the DateInput's own format. */
export const defaultFallReportingDate = (collectionYear) => {
    const fallYear = fallYearOf(collectionYear);
    return fallYear ? `${fallYear}-10-15T00:00:00` : null;
};

/** "2026–27 (Fall 2026)" */
export const collectionYearLabel = (t, year) => t('ipedsWizard.collectionYearOption', {
    ns: 'tools',
    span: year.span,
    fallYear: year.startYear
});

// NCES alternates the two optional parts: age (Part B) is required for odd fall years and
// residence (Part C) for even ones. Each is optional in the other year.
export const isPartBRequired = (fallYear) => fallYear % 2 === 1;
export const isPartCRequired = (fallYear) => fallYear % 2 === 0;

/**
 * The EF parts this run will write, as [part code, i18n key]. Part B and Part C appear when the
 * year requires them, or when the user has opted in to an optional one. Part D (entering class)
 * applies to EF1 academic reporters, and to EF2 academic reporters or Other academic year.
 */
export const getPartsIncluded = ({ collectionYear, institutionType, calendarSystemId, includePartB, includePartC }) => {
    const fallYear = fallYearOf(collectionYear);
    const template = efTemplateFor(institutionType);
    const parts = [['A', 'ipedsWizard.efPartA']];

    if (fallYear) {
        if (isPartBRequired(fallYear)) parts.push(['B', 'ipedsWizard.efPartBRequired']);
        else if (includePartB) parts.push(['B', 'ipedsWizard.efPartBOptional']);

        if (isPartCRequired(fallYear)) parts.push(['C', 'ipedsWizard.efPartCRequired']);
        else if (includePartC) parts.push(['C', 'ipedsWizard.efPartCOptional']);
    }

    const academic = isAcademicReporter(calendarSystemId);
    if ((template === 'EF1' && academic)
        || (template === 'EF2' && (academic || calendarSystemId === CALENDAR_OTHER_ACADEMIC_YEAR))) {
        parts.push(['D', 'ipedsWizard.efPartD']);
    }

    ['E', 'F', 'G', 'H'].forEach((part) => parts.push([part, `ipedsWizard.efPart${part}`]));
    return parts;
};

/**
 * FTE is typed with at most one decimal place. Anything that would not lead to such a number is
 * refused at the keystroke rather than reported afterwards.
 */
export const isFteInput = (value) => /^\d*(\.\d?)?$/.test(value);

const isPositiveFte = (value) => value !== '' && value !== '.' && Number(value) > 0;

// DateInput stores "YYYY-MM-DDTHH:mm:ss" and writes "Invalid date" for a half-typed value.
const isDateValue = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value);

/**
 * Field → { key, field } for every rule the EF fields break. `field` is the i18n key of the name
 * the message uses, so "{Field} Required" reads e.g. "Fall Term Required".
 */
export const getEfParameterErrors = (parameters, setup) => {
    const errors = {};
    const required = (name, labelKey) => { errors[name] = { key: 'ipedsWizard.fieldRequired', field: labelKey }; };

    if (!parameters.collectionYear) required('collectionYear', 'ipedsWizard.collectionYear');
    if (!parameters.fallTermId) required('fallTermId', 'ipedsWizard.fallTerm');

    // Program reporters have a fixed window, not a date to enter.
    if (isAcademicReporter(setup?.ipedsCalendarSystemId)) {
        if (!parameters.fallReportingDate) required('fallReportingDate', 'ipedsWizard.fallReportingDate');
        else if (!isDateValue(parameters.fallReportingDate)) errors.fallReportingDate = { key: 'ipedsWizard.invalidDate', field: 'ipedsWizard.fallReportingDate' };
    }

    if (parameters.fteInstructionalStaff === '' || parameters.fteInstructionalStaff == null) {
        required('fteInstructionalStaff', 'ipedsWizard.fteInstructionalStaffName');
    } else if (!isPositiveFte(parameters.fteInstructionalStaff)) {
        errors.fteInstructionalStaff = { key: 'ipedsWizard.efFteGreaterThanZero', field: 'ipedsWizard.fteInstructionalStaffName' };
    }

    return errors;
};

// Two-letter codes IPEDS accepts for an institution's state: the states, DC and the outlying areas.
export const IPEDS_STATE_CODES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
    'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC',
    'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'AS', 'FM', 'GU', 'MH', 'MP', 'PR', 'PW', 'VI'
];

/**
 * Problems that stop the extract, as Before You Continue lists them: [code, i18n key]. Next stays
 * disabled while any is listed. These are setup, not snapshots — no snapshot state ever appears here.
 */
export const getEfStopProblems = (parameters, setup) => {
    const problems = [];
    const errors = getEfParameterErrors(parameters, setup);

    if (!setup?.ipedsInstitutionType) problems.push(['X01', 'ipedsWizard.efStopX01']);
    if (!setup?.ipedsCalendarSystemId) problems.push(['X02', 'ipedsWizard.efStopX02']);
    if (errors.fallReportingDate) problems.push(['X03', 'ipedsWizard.efStopX03']);
    if (errors.fteInstructionalStaff) problems.push(['X04', 'ipedsWizard.efStopX04']);
    if (!setup?.ncesUnitId) problems.push(['X05', 'ipedsWizard.efStopX05']);
    if (!IPEDS_STATE_CODES.includes(String(setup?.institutionStateProv || '').toUpperCase())) {
        problems.push(['X06', 'ipedsWizard.efStopX06']);
    }
    return problems;
};

/** Notices, listed for information only: [code, i18n key, interpolation values]. */
export const getEfNotices = (setup) => {
    const grades = setup?.withdrawalGradesNotCountingAttemptedHours || [];
    return grades.length
        ? [['B01', 'ipedsWizard.efNoticeB01', { grades: grades.join(', ') }]]
        : [];
};

/** Everything that keeps Next disabled on Step 2: the field rules plus the stop problems. */
export const getEfBlockingErrors = (parameters, setup) => {
    const errors = { ...getEfParameterErrors(parameters, setup) };
    getEfStopProblems(parameters, setup).forEach(([code, key]) => { errors[code] = { key }; });
    return errors;
};
